import Button from 'components/shared/Button/Button';

/**
 * Helper for sub-modal content that needs a reactive footer.
 * Handles the flex layout so you don't have to repeat it.
 *
 * Usage with openSubModal:
 * ```js
 * openSubModal({
 *   title: 'Create folder',
 *   footer: false,
 *   className: '!overflow-hidden !p-0',
 *   content: (
 *     <SubModalForm
 *       onClose={() => closeSubModal()}
 *       footer={({ onClose }) => (
 *         <>
 *           <Button text="Cancel" onClick={onClose} style="outlined" color="neutral" size="small" />
 *           <Button text="Create" onClick={handleCreate} isDisabled={!name} size="small" />
 *         </>
 *       )}
 *     >
 *       <Input label="Name" value={name} onChange={...} />
 *     </SubModalForm>
 *   ),
 *   actions: { onClose: () => closeSubModal() }
 * });
 * ```
 *
 * Or with the simpler props for the common Cancel + Submit pattern:
 * ```js
 * <SubModalForm
 *   onClose={() => closeSubModal()}
 *   submitText="Create"
 *   onSubmit={handleCreate}
 *   isDisabled={!name}
 *   isLoading={loading}
 * >
 *   <Input label="Name" ... />
 * </SubModalForm>
 * ```
 */
const SubModalForm = ({
  children,
  onClose,
  className,
  // Simple mode props
  submitText,
  onSubmit,
  isDisabled,
  submitTooltip,
  isLoading,
  // Custom footer render function: ({ onClose }) => ReactNode
  footer
}) => {
  const renderFooter = () => {
    if (footer) {
      return footer({ onClose });
    }

    return (
      <>
        <Button text="Cancel" size="small" style="outlined" color="neutral" onClick={onClose} />
        {onSubmit && (
          <Button
            text={submitText || 'Submit'}
            size="small"
            onClick={onSubmit}
            isLoading={isLoading}
            isDisabled={isDisabled}
            tooltip={submitTooltip}
            tooltipPlacement="top"
          />
        )}
      </>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className={className || 'flex-1 overflow-y-auto p-4'}>{children}</div>
      <div className="flex h-[56px] shrink-0 items-center justify-end gap-2 border-0 border-t border-solid border-neutral-100 bg-white px-4 py-3">
        {renderFooter()}
      </div>
    </div>
  );
};

export default SubModalForm;
